import ManageAccounts from 'components/accounts/ManageAccounts';
import CompanyOwnedTools from 'components/company/company-owned-tools/CompanyOwnedTools';
import { ManageCompanyRouteParams, ManageCompanyTab } from 'components/company/manageCompanyNavConstants';
import ManageInvoiceInfo from 'components/company/ManageInvoiceInfo';
import ManageInvoiceList from 'components/company/ManageInvoiceList';
import ManageSettings from 'components/company/ManageSettings';
import ManageMembers from 'components/member/ManageMembers';
import { ProcessStatus } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, RouteComponentProps } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { useToolsListForOwner } from 'services/api/companyOwnedToolsQueries';
import CompaniesService from 'services/CompanyService';
import styled from 'styled-components';
import { Aside, Container, H2, MainView } from 'styles/Styles.styled';

type TabModel = {
  tabId: ManageCompanyTab;
  name: string;
  component: React.ReactNode;
  hidden?: boolean;
};

const ManageCompany: React.FC<RouteComponentProps<ManageCompanyRouteParams>> = ({
  match: {
    params: { companyId, tab },
  },
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<ProcessStatus>(ProcessStatus.None);
  const [hasUserPermissionEditCompanySetting, setUserPermissionStatusEditCompanySetting] = React.useState<boolean>(false);
  const { company, changeCompany, companyAccountId } = React.useContext(CompanyContext);
  const ownedTools = useToolsListForOwner(companyId).data;
  const activeTab = tab || 'members';

  const tabs: TabModel[] = [
    {
      tabId: 'members',
      name: t('modelsToEdit.members'),
      component: <ManageMembers />,
      hidden: false,
    },
    {
      tabId: 'accounts',
      name: t('modelsToEdit.accounts'),
      component: <ManageAccounts companyId={companyId} />,
      hidden: false,
    },
    {
      tabId: 'settings',
      name: t('modelsToEdit.settings'),
      component: <ManageSettings companyId={companyId} />,
      hidden: false,
    },
    {
      tabId: 'invoice',
      name: t('modelsToEdit.invoiceInfo'),
      component: <ManageInvoiceInfo companyId={companyId} />,
      hidden: false,
    },
    {
      tabId: 'invoiceList',
      name: t('modelsToEdit.invoiceList'),
      component: (
        <ManageInvoiceList companyId={companyId} companyName={company?.name} accountId={companyAccountId as string} />
      ),
      hidden: false,
    },
    {
      tabId: 'tools',
      name: t('modelsToEdit.tools'),
      component: ownedTools && <CompanyOwnedTools companyId={companyId} />,
      hidden: !ownedTools || ownedTools.length === 0,
    },
  ];

  React.useEffect(() => {
    const fetchCompany = async () => {
      try {
        const accessToEditCompany = await CompaniesService.getAccessToEdit(companyId || '');
        setUserPermissionStatusEditCompanySetting(accessToEditCompany);
        setStatus(ProcessStatus.Fetching);
        changeCompany && changeCompany(companyId);
        setStatus(ProcessStatus.Success);
      } catch {
        setStatus(ProcessStatus.Error);
      }
    };
    if (companyId && companyId !== '') {
      fetchCompany();
    }
  }, []);

  React.useEffect(() => {
    const fetchCompany = async () => {
      try {
        if (company && companyId && company.id !== companyId) {
          setStatus(ProcessStatus.Fetching);
          changeCompany && changeCompany(companyId);
          setStatus(ProcessStatus.Success);
        }
      } catch {
        setStatus(ProcessStatus.Error);
      }
    };
    if (companyId && companyId !== '') {
      fetchCompany();
    }
  }, [company]);

  if (!hasUserPermissionEditCompanySetting) {
    return null;
  }

  return (
    <Container>
      <MainView>
        <H2>{company?.name}&nbsp;</H2>
        <Tabs>
          {tabs
            .filter((t) => !t.hidden)
            .map((t) => (
              <Tab key={t.tabId} to={EpdLinks.manageCompany(companyId, t.tabId)} $active={t.tabId === activeTab}>
                {t.name}
              </Tab>
            ))}
        </Tabs>
        {tabs.find((t) => !t.hidden && t.tabId === activeTab)?.component}
      </MainView>
      {status && <Aside style={{ backgroundColor: 'white' }}> </Aside>}
    </Container>
  );
};

const Tabs = styled.div`
  display: flex;
  flex-direction: row;
  border-bottom: solid 3px ${(props) => props.theme.colors.gray};
`;

const Tab = styled(Link)<{ $active: boolean }>`
  display: block;
  ${(props) => props.theme.fonts.heading3};
  color: #000000;
  padding: 1rem;
  margin-right: 1px;
  background-color: ${(props) => (props.$active ? props.theme.colors.gray : 'white')};
  border: solid 1px ${(props) => props.theme.colors.gray};
  border-top: solid 3px ${(props) => props.theme.colors.regionColorLight};
  cursor: pointer;
  text-decoration: none;
  &:hover {
    border-top-color: ${(props) => props.theme.colors.regionColor};
  }
`;

export default ManageCompany;
